import React, { useCallback, useEffect, useState } from 'react';

import { useApiMetaDiscountExperimentRetrieve } from '@rover/rsdk/src/apiClient/latest/api-meta-discount-experiment-retrieve';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';

import {
  MetaDiscountBottomSheetCollapsedCtaClick,
  MetaDiscountBottomSheetCtaClick,
  MetaDiscountBottomSheetDismissed,
  MetaDiscountBottomSheetShow,
} from './analyticsEvents';
import { META_DISCOUNT_TREATMENT_ARM } from './constants';
import {
  buildSignupUrl,
  getSessionId,
  hasDismissedBottomSheet,
  markBottomSheetDismissed,
} from './helpers';
import MetaDiscountBanner from './MetaDiscountBanner';
import MetaDiscountSheet from './MetaDiscountSheet';

type Mode = 'sheet' | 'banner';

const MetaDiscountOffer = (): JSX.Element | null => {
  const { data } = useApiMetaDiscountExperimentRetrieve();
  const isVariant = data?.arm === META_DISCOUNT_TREATMENT_ARM;

  const [mode, setMode] = useState<Mode>(() => (hasDismissedBottomSheet() ? 'banner' : 'sheet'));

  useEffect(() => {
    if (!isVariant || mode !== 'sheet') return;
    emitAnalyticsEvent(new MetaDiscountBottomSheetShow({ session_id: getSessionId() }));
  }, [isVariant, mode]);

  const goToSignup = useCallback(() => {
    const { pathname, search } = window.location;
    window.location.assign(buildSignupUrl(pathname, search));
  }, []);

  const onDismiss = useCallback(() => {
    markBottomSheetDismissed();
    emitAnalyticsEvent(new MetaDiscountBottomSheetDismissed({ session_id: getSessionId() }));
    setMode('banner');
  }, []);

  const onSheetCtaClick = useCallback(() => {
    emitAnalyticsEvent(new MetaDiscountBottomSheetCtaClick({ session_id: getSessionId() }));
    goToSignup();
  }, [goToSignup]);

  const onBannerCtaClick = useCallback(() => {
    emitAnalyticsEvent(
      new MetaDiscountBottomSheetCollapsedCtaClick({ session_id: getSessionId() })
    );
    goToSignup();
  }, [goToSignup]);

  if (!isVariant) return null;

  return mode === 'sheet' ? (
    <MetaDiscountSheet onCtaClick={onSheetCtaClick} onDismiss={onDismiss} />
  ) : (
    <MetaDiscountBanner onCtaClick={onBannerCtaClick} />
  );
};

export default MetaDiscountOffer;
